import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppDispatch } from '@/shared/infra/store/hooks';
import { setAccount, setHasSelectedAccount } from '@/shared/infra/store/authSlice';
import { useAuthDIContext } from '../../di/AuthDIContext';
import Account from '@/features/account/application/domain/entities/account';

interface UseAccountSelectionViewModelProps {
  onClose?: () => void;
}

export const useAccountSelectionViewModel = ({ onClose }: UseAccountSelectionViewModelProps = {}) => {
  const dispatch = useAppDispatch();
  const authDIContext = useAuthDIContext();
  const queryClient = useQueryClient();

  if (!authDIContext) {
    throw new Error('useAccountSelectionViewModel must be used within AuthDIProvider');
  }

  // Fetch account list
  const {
    data: accountList = [],
    isLoading: isLoadingAccounts,
    error: accountListError,
  } = useQuery({
    queryKey: ['accountList'],
    queryFn: () => authDIContext.authUseCase.getAccountList(),
  });

  // Select account mutation
  const {
    mutate: selectAccount,
    isPending: isSelectingAccount,
    error: selectionError,
  } = useMutation({
    mutationFn: (accountId: string) => {
      return authDIContext.authUseCase.selectAccount(accountId);
    },
    onSuccess: (account: Account | null) => {
      // Dispatch the full account data from the API response
      dispatch(setAccount(account));
      dispatch(setHasSelectedAccount(true));

      // Invalidate dashboard queries so they refetch with the new account
      queryClient.invalidateQueries({ queryKey: ['dashboardPendingCounts'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardReportData'] });

      // Close the dialog on successful account selection
      if (onClose) {
        onClose();
      }
    },
  });

  return {
    accountList,
    isLoadingAccounts,
    accountListError,
    selectAccount,
    isSelectingAccount,
    selectionError,
  };
};
